'use client';

import { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import { EditorInstance } from 'novel';
import { SuggestionItem, getFilteredBaseItems } from '../components/slash-command';

interface SlashCommandPosition {
    x: number;
    y: number;
    isAbove: boolean;
}

interface UseSlashCommandOptions {
    editorInstance: EditorInstance | null;
}

export function useSlashCommand({ editorInstance }: UseSlashCommandOptions) {
    const [isOpen, setIsOpen] = useState(false);
    const [items, setItems] = useState<SuggestionItem[]>([]);
    const [query, setQuery] = useState('');
    const [selected, setSelected] = useState(0);
    const [position, setPosition] = useState<SlashCommandPosition | null>(null);

    const menuRef = useRef<HTMLDivElement | null>(null);
    const inputRef = useRef<HTMLInputElement | null>(null);

    // Filter items based on search query
    const filteredItems = useMemo(() => {
        if (!query.trim()) return items;
        const q = query.toLowerCase();
        return items.filter(item => {
            return (
                item.title.toLowerCase().includes(q) ||
                item.description.toLowerCase().includes(q) ||
                item.searchTerms?.some(term => term.toLowerCase().includes(q))
            );
        });
    }, [items, query]);

    // Reset selection when filtered items change
    useEffect(() => {
        setSelected(0);
    }, [filteredItems]);

    // Open slash command palette at current cursor position
    const openAtCursor = useCallback(() => {
        if (!editorInstance) return;

        editorInstance.chain().focus().scrollIntoView().run();

        const posNum = editorInstance.state.selection.from;
        const coords = editorInstance.view.coordsAtPos(posNum);
        const menuItems = getFilteredBaseItems((editorInstance as unknown) as any);

        // Calculate menu position with viewport boundary detection
        const menuHeight = 400;
        const menuWidth = 288;
        const gap = 6;

        const viewportHeight = window.innerHeight;
        const viewportWidth = window.innerWidth;

        const wouldOverflowBottom = coords.bottom + gap + menuHeight > viewportHeight;
        const wouldOverflowRight = coords.left + menuWidth > viewportWidth;

        let x = coords.left;
        let y = coords.bottom + gap;
        let isAbove = false;

        if (wouldOverflowBottom) {
            y = coords.top - gap;
            isAbove = true;
        }

        if (wouldOverflowRight) {
            x = viewportWidth - menuWidth - 10;
        }

        setItems(menuItems);
        setSelected(0);
        setPosition({ x, y, isAbove });
        setIsOpen(true);
    }, [editorInstance]);

    // Close the palette
    const close = useCallback(() => {
        setIsOpen(false);
    }, []);

    // Execute selected command
    const executeSelected = useCallback(() => {
        if (!editorInstance || !filteredItems.length) return;

        const item = filteredItems[selected];
        if (item) {
            const posNum = editorInstance.state.selection.from;
            const range = { from: posNum, to: posNum } as any;
            (item as any).command({ editor: (editorInstance as unknown) as any, range });
            close();
        }
    }, [editorInstance, filteredItems, selected, close]);

    // Handle keyboard navigation
    const handleKeydown = useCallback((event: KeyboardEvent): boolean => {
        if (!isOpen) return false;

        const key = event.key;

        if (key === 'Escape') {
            event.preventDefault();
            close();
            return true;
        }

        const isInputFocused = document.activeElement === inputRef.current;

        if (isInputFocused) {
            if (key === 'ArrowDown' || key === 'ArrowUp' || key === 'Enter') {
                event.preventDefault();

                if (key === 'ArrowDown' && filteredItems.length > 0) {
                    setSelected((i) => (i + 1) % filteredItems.length);
                    return true;
                }
                if (key === 'ArrowUp' && filteredItems.length > 0) {
                    setSelected((i) => (i - 1 + filteredItems.length) % filteredItems.length);
                    return true;
                }
                if (key === 'Enter' && filteredItems.length > 0) {
                    executeSelected();
                    return true;
                }
            }
            return false;
        }

        if (!filteredItems.length) return false;

        if (key === 'ArrowDown') {
            event.preventDefault();
            setSelected((i) => (i + 1) % filteredItems.length);
            return true;
        }
        if (key === 'ArrowUp') {
            event.preventDefault();
            setSelected((i) => (i - 1 + filteredItems.length) % filteredItems.length);
            return true;
        }
        if (key === 'Enter') {
            event.preventDefault();
            executeSelected();
            return true;
        }
        return false;
    }, [isOpen, filteredItems, executeSelected, close]);

    // Focus search input when menu opens
    useEffect(() => {
        if (isOpen) {
            setQuery('');
            const focusTimer = setTimeout(() => {
                inputRef.current?.focus();
            }, 100);
            inputRef.current?.focus();
            return () => clearTimeout(focusTimer);
        }
    }, [isOpen]);

    // Close on outside click
    useEffect(() => {
        if (!isOpen) return;
        const onDown = (e: MouseEvent) => {
            if (!menuRef.current) return;
            if (!menuRef.current.contains(e.target as Node)) {
                close();
            }
        };
        document.addEventListener('mousedown', onDown);
        return () => document.removeEventListener('mousedown', onDown);
    }, [isOpen, close]);

    // Listen for open-slash-shortcut event
    useEffect(() => {
        const handler = () => openAtCursor();
        document.addEventListener('open-slash-shortcut', handler);
        return () => document.removeEventListener('open-slash-shortcut', handler);
    }, [openAtCursor]);

    // Global keyboard listener
    useEffect(() => {
        if (!isOpen) return;
        const listener = (e: KeyboardEvent) => handleKeydown(e);
        document.addEventListener('keydown', listener);
        return () => document.removeEventListener('keydown', listener);
    }, [isOpen, handleKeydown]);

    return {
        isOpen,
        items: filteredItems,
        query,
        setQuery,
        selected,
        setSelected,
        position,
        menuRef,
        inputRef,
        close,
        executeSelected,
        handleKeydown,
    };
}
